import React, { useState } from 'react';
import { X, Lock, Unlock, Copy, Check } from 'lucide-react';
import { useStore } from '../store/useStore';
import { SyntaxHighlighter } from './SyntaxHighlighter';
import { VariableHighlighter } from './VariableHighlighter';
import { FilterConditionDisplay } from './FilterConditionDisplay';
import { detectVariables } from '../utils/variableParser';
import { hasFilterConditions } from '../utils/filterParser';

export const DetailPanel: React.FC = () => {
  const { selectedNode, setDetailPanelOpen, isUnlocked, getCurrentWorkflow } = useStore();
  const [copiedField, setCopiedField] = useState<string | null>(null);

  if (!selectedNode) return null;

  const handleGetLicense = () => {
    const checkoutUrl = import.meta.env.VITE_LEMON_SQUEEZY_CHECKOUT_URL;

    if (checkoutUrl && checkoutUrl !== 'https://store.lemonsqueezy.com/checkout') {
      window.open(checkoutUrl, '_blank', 'noopener,noreferrer');
    } else {
      alert(
        'Checkout URL not configured.\n\n' +
        'Please set VITE_LEMON_SQUEEZY_CHECKOUT_URL in .env.local\n\n' +
        'See LEMON_SQUEEZY_SETUP.md for instructions.'
      );
    }
  };

  const { label, app, event, nodeType, config, rawNode, stepTitle } = selectedNode.data;

  const handleCopy = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const renderConfigValue = (value: any): {
    isComplex: boolean;
    display: string;
    language?: 'json' | 'javascript' | 'python';
    hasVariables?: boolean;
  } => {
    if (value === null || value === undefined) {
      return { isComplex: false, display: '' };
    }

    if (typeof value === 'object') {
      return {
        isComplex: true,
        display: JSON.stringify(value, null, 2),
        language: 'json'
      };
    }

    // Check if it's code (multi-line string that looks like code)
    const stringValue = String(value);
    if (stringValue.includes('\n') && stringValue.length > 100) {
      // Detect language from content
      let language: 'javascript' | 'python' | undefined;
      if (stringValue.includes('def ') || stringValue.includes('import ')) {
        language = 'python';
      } else if (stringValue.includes('function') || stringValue.includes('const ') || stringValue.includes('let ')) {
        language = 'javascript';
      }

      if (language) {
        return { isComplex: true, display: stringValue, language };
      }
    }

    // Check if string contains variables
    const variables = detectVariables(stringValue);
    const hasVariables = variables.length > 0;

    return { isComplex: false, display: stringValue, hasVariables };
  };

  const hasConfig = config && Object.keys(config).length > 0;
  const workflow = getCurrentWorkflow();
  const allNodes = workflow?.nodes || [];

  return (
    <div className="fixed inset-y-0 right-0 w-full sm:w-[480px] bg-card border-l border-border shadow-2xl z-50 flex flex-col animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border bg-muted/30">
        <h2 className="text-lg font-semibold">Step Details</h2>
        <button
          onClick={() => setDetailPanelOpen(false)}
          className="p-2 hover:bg-accent rounded-lg transition-colors"
          aria-label="Close panel"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-6 space-y-6">
          {/* Step Type Badge */}
          <div>
            <span
              className={`
              inline-flex items-center px-3 py-1 rounded-full text-xs font-medium uppercase tracking-wide
              ${
                nodeType === 'trigger'
                  ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                  : nodeType === 'action'
                  ? 'bg-blue-500/10 text-blue-700 dark:text-blue-400'
                  : nodeType === 'filter'
                  ? 'bg-amber-500/10 text-amber-700 dark:text-amber-400'
                  : nodeType === 'code'
                  ? 'bg-purple-500/10 text-purple-700 dark:text-purple-400'
                  : nodeType === 'branch'
                  ? 'bg-pink-500/10 text-pink-700 dark:text-pink-400'
                  : 'bg-gray-500/10 text-gray-700 dark:text-gray-400'
              }
            `}
            >
              {nodeType}
            </span>
          </div>

          {/* Title */}
          <div>
            <h3 className="text-2xl font-bold text-card-foreground mb-2">{label}</h3>
            {stepTitle && stepTitle !== label && (
              <p className="text-sm text-muted-foreground">{stepTitle}</p>
            )}
          </div>

          {/* App & Event */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                App
              </p>
              <p className="text-sm font-medium">{app}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                Event
              </p>
              <p className="text-sm font-medium">{event}</p>
            </div>
          </div>

          {/* Filter Conditions Section (for filter nodes) */}
          {nodeType === 'filter' && hasFilterConditions(config, rawNode as any) && (
            <div>
              <FilterConditionDisplay
                config={config}
                params={(rawNode as any)?.params}
                isUnlocked={isUnlocked}
              />
            </div>
          )}

          {/* Configuration Section - The Freemium Lock */}
          <div className="relative">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold uppercase tracking-wide">
                Configuration
              </h4>
              {!isUnlocked && (
                <div className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                  <Lock className="w-3 h-3" />
                  <span>Locked</span>
                </div>
              )}
            </div>

            <div className={`relative ${!isUnlocked ? 'locked-content' : ''}`}>
              {hasConfig ? (
                <div className="space-y-3 bg-muted/50 rounded-lg p-4 border border-border">
                  {Object.entries(config).map(([key, value]) => {
                    const rendered = renderConfigValue(value);
                    return (
                      <div key={key} className="group">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-muted-foreground mb-1">
                              {key}
                            </p>
                            {rendered.isComplex && rendered.language ? (
                              <SyntaxHighlighter
                                code={rendered.display}
                                language={rendered.language}
                                maxHeight="400px"
                              />
                            ) : rendered.hasVariables ? (
                              <VariableHighlighter
                                text={rendered.display}
                                currentNode={selectedNode}
                                allNodes={allNodes}
                                className="text-sm bg-background/50 px-2 py-1 rounded"
                                configValue={value}
                              />
                            ) : (
                              <p className="text-sm font-mono break-all bg-background/50 px-2 py-1 rounded">
                                {rendered.display}
                              </p>
                            )}
                          </div>
                          {isUnlocked && (
                            <button
                              onClick={() => handleCopy(rendered.display, key)}
                              className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-accent rounded flex-shrink-0"
                              aria-label="Copy value"
                            >
                              {copiedField === key ? (
                                <Check className="w-4 h-4 text-emerald-500" />
                              ) : (
                                <Copy className="w-4 h-4" />
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground italic">
                  No configuration available
                </p>
              )}

              {/* Blur overlay for locked content */}
              {!isUnlocked && hasConfig && (
                <div className="absolute inset-0 flex items-center justify-center bg-background/40 backdrop-blur-sm rounded-lg">
                  <div className="text-center p-6 bg-card/90 rounded-lg shadow-lg border border-border max-w-xs">
                    <Lock className="w-12 h-12 mx-auto mb-3 text-amber-500" />
                    <h5 className="font-semibold mb-2">🔓 Unlock Pro Features</h5>
                    <p className="text-sm text-muted-foreground mb-4">
                      View complete step configuration, mapped fields, code snippets, and more
                    </p>
                    <button
                      onClick={handleGetLicense}
                      className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium mb-2"
                    >
                      Get Pro License - $29
                    </button>
                    <p className="text-xs text-muted-foreground">
                      <strong>Lifetime access</strong> • Pay once, use forever
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Raw Data Section (also locked) */}
          {isUnlocked && (
            <div>
              <h4 className="text-sm font-semibold uppercase tracking-wide mb-3">
                Raw Data
              </h4>
              <div className="relative">
                <SyntaxHighlighter
                  code={JSON.stringify(rawNode, null, 2)}
                  language="json"
                  maxHeight="400px"
                />
                <button
                  onClick={() => handleCopy(JSON.stringify(rawNode, null, 2), 'raw')}
                  className="absolute top-2 right-2 p-2 bg-card/80 hover:bg-accent rounded transition-colors"
                  aria-label="Copy raw data"
                >
                  {copiedField === 'raw' ? (
                    <Check className="w-4 h-4 text-emerald-500" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
          )}

          {!isUnlocked && (
            <div className="bg-muted/30 border border-border rounded-lg p-4 text-center">
              <Unlock className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                Unlock to view raw node data, mapped variables, and advanced details
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-border p-4 bg-muted/30">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Node ID: {selectedNode.id}</span>
          <span className="flex items-center gap-1">
            {isUnlocked ? (
              <>
                <Unlock className="w-3 h-3" />
                Unlocked
              </>
            ) : (
              <>
                <Lock className="w-3 h-3" />
                Free Preview
              </>
            )}
          </span>
        </div>
      </div>
    </div>
  );
};
